// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { abbreviateCaseBuddyHash, caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { CASE_COLUMN_HEADERS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_INCONSISTENCY_RULES = Object.freeze({
  PATH_ENDS_NOTES_TO_Q_MISMATCH: 'path_ends_notes_to_q_mismatch',
  PATH_ENDS_Q_TO_NOTES_MISMATCH: 'path_ends_q_to_notes_mismatch',
  BRANCHES_AS_VS_PATH_ENDS: 'branches_as_vs_path_ends',
  UNKNOWN_REFERENCED_IDS: 'unknown_referenced_ids',
  PATH_ENDS_BUT_NO_FUNDS_FOLLOWED: 'path_ends_but_no_funds_followed',
  CONTINUES_AS_SINGLE_NEXT_ENTRY_MISMATCH: 'continues_as_single_next_entry_mismatch',
  POSSIBLE_COPIED_ENDPOINT_ATTRIBUTION: 'possible_copied_endpoint_attribution'
});

export const CASE_INCONSISTENCY_FIX_CODES = Object.freeze({
  SAFE_ADD_CONTINUES_NOTE: 'safe_add_continues_note',
  JUDGEMENT_SET_SUBROW_Q_PATH_ENDS: 'judgement_set_subrow_q_path_ends',
  JUDGEMENT_ADD_EXPLICIT_CONTINUATION: 'judgement_add_explicit_continuation',
  JUDGEMENT_REMOVE_STRAY_PATH_ENDS: 'judgement_remove_stray_path_ends',
  JUDGEMENT_ADD_PATH_ENDS_NOTE: 'judgement_add_path_ends_note'
});

export function normalizeCaseInconsistencyOptions(options = {}) {
  return {
    rulePathEndsMismatch: options.rulePathEndsMismatch !== false,
    ruleBranchesAsVsPathEnds: options.ruleBranchesAsVsPathEnds !== false,
    ruleUnknownHashes: options.ruleUnknownHashes !== false,
    rulePathEndsButNoFundsFollowed: options.rulePathEndsButNoFundsFollowed !== false,
    ruleContinuesAsSingleButNextEntryMismatch: options.ruleContinuesAsSingleButNextEntryMismatch !== false,
    rulePossibleCopiedEndpointAttribution: options.rulePossibleCopiedEndpointAttribution !== false
  };
}

export function isCaseInconsistencyPathEndsText(value) {
  const safe = normalizeCell(value).toLowerCase();
  return safe === 'path ends.' || safe === 'path ends';
}

export function isCaseInconsistencyContinuesAsText(value) {
  return normalizeCell(value).toLowerCase().startsWith('continues as');
}

export function caseInconsistencyIsAllHexChars(value) {
  const text = normalizeCell(value);
  if (!text) return false;
  for (let index = 0; index < text.length; index += 1) {
    const ch = text[index];
    const digit = ch >= '0' && ch <= '9';
    const lower = ch >= 'a' && ch <= 'f';
    const upper = ch >= 'A' && ch <= 'F';
    if (!(digit || lower || upper)) return false;
  }
  return true;
}

export function caseInconsistencyIsHexLike(value) {
  const text = normalizeCell(value);
  if (!text) return false;
  if (text.length >= 2 && text[0] === '0' && (text[1] === 'x' || text[1] === 'X')) return true;
  return caseInconsistencyIsAllHexChars(text);
}

export function normalizeCaseInconsistencyId(value) {
  const text = normalizeCell(value);
  if (!text) return '';
  if (caseInconsistencyIsHexLike(text)) return text.toLowerCase();
  return text;
}

export function stripCaseInconsistencyEdgePunctuation(value) {
  let token = normalizeCell(value);
  const punctuation = '.,;:()[]{}<>"\'';
  while (token && punctuation.includes(token[0])) token = token.slice(1);
  while (token && punctuation.includes(token[token.length - 1])) token = token.slice(0, -1);
  return token.trim();
}

export function splitCaseInconsistencyWhitespace(value) {
  const text = normalizeCell(value);
  return text ? text.split(/\s+/).filter(Boolean) : [];
}

export function extractCaseInconsistencyIdsFromCell(value) {
  const original = normalizeCell(value);
  if (!original) return [];
  const lower = original.toLowerCase();
  if (isCaseInconsistencyPathEndsText(lower)) return [];
  let text = original;
  if (lower.startsWith('continues as')) text = text.slice(12);
  if (text.toLowerCase().startsWith('from ')) text = text.slice(5);

  const ids = [];
  text.split(',').forEach((part) => {
    splitCaseInconsistencyWhitespace(part).forEach((rawToken) => {
      const token = stripCaseInconsistencyEdgePunctuation(rawToken);
      if (!token || token.length < 2) return;
      const normalized = token.toLowerCase();
      if (['as', 'and', 'or'].includes(normalized)) return;
      ids.push(token);
    });
  });
  return ids;
}

export function buildCaseInconsistencyNotesWithPathEnds(existingNotes) {
  const base = normalizeCell(existingNotes);
  if (!base) return 'Path ends.';
  if (base.toLowerCase().includes('path ends')) return base;
  let cleaned = base;
  while (/[.,;]$/.test(cleaned)) cleaned = cleaned.slice(0, -1).trim();
  return `${cleaned}. Path ends.`;
}

export function buildCaseInconsistencyNotesWithoutPathEnds(existingNotes) {
  let updated = normalizeCell(existingNotes);
  if (!updated) return '';
  updated = updated.replace(/Path ends\./gi, '').replace(/Path ends/gi, '');
  updated = updated.replace(/\.\./g, '.').replace(/\s{2,}/g, ' ').replace(/\s+,/g, ',').replace(/\s+\./g, '.').trim();
  while (updated.startsWith('.') || updated.startsWith(',')) updated = updated.slice(1).trimStart();
  return updated;
}

export function buildCaseInconsistencyNotesWithContinues(existingNotes, addition) {
  const base = normalizeCell(existingNotes);
  const add = normalizeCell(addition);
  if (!add) return base;
  if (!base) return add;
  let cleaned = base;
  if (cleaned.endsWith('.')) cleaned = cleaned.slice(0, -1).trim();
  if (cleaned.endsWith(',')) return `${cleaned} ${add}`;
  return `${cleaned}, ${add}`;
}

export function buildCaseInconsistencyNotesWithExplicitContinuation(existingNotes, targetHash) {
  return buildCaseInconsistencyNotesWithContinues(existingNotes, `Continues as ${normalizeCell(targetHash)}`);
}

export function buildCaseInconsistencyCombinedNotes(rowRefs = []) {
  return (Array.isArray(rowRefs) ? rowRefs : [])
    .map(rowRef => normalizeCell(rowRef?.row?.[9]))
    .filter(Boolean)
    .join(' ');
}

export function firstNonEmptyCaseInconsistencyEntryValue(rowRefs = [], colIndex = 0) {
  for (const rowRef of Array.isArray(rowRefs) ? rowRefs : []) {
    const value = normalizeCell(rowRef?.row?.[colIndex]);
    if (value) return value;
  }
  return '';
}

export function caseInconsistencyEntryStillHasAnyPathEndsNotesToQMismatch(rowRefs = []) {
  for (const rowRef of Array.isArray(rowRefs) ? rowRefs : []) {
    const row = Array.isArray(rowRef?.row) ? rowRef.row : [];
    if (!rowHasMeaningfulData(row)) continue;
    const notesHasPathEnds = normalizeCell(row[9]).toLowerCase().includes('path ends');
    const qIsPathEnds = isCaseInconsistencyPathEndsText(row[16]);
    if (notesHasPathEnds && !qIsPathEnds) return true;
  }
  return false;
}

export function caseInconsistencyNotesMentionsId(notesRaw, notesLower, idRaw) {
  const target = normalizeCell(idRaw);
  if (!target) return false;
  if (caseInconsistencyIsHexLike(target)) return normalizeCell(notesLower).includes(target.toLowerCase());
  return normalizeCell(notesRaw).includes(target) || normalizeCell(notesLower).includes(target.toLowerCase());
}

export function caseInconsistencyNotesMentionsLabel(notesRaw, notesLower, label) {
  const target = normalizeCell(label);
  if (!target) return false;
  return normalizeCell(notesRaw).includes(target) || normalizeCell(notesLower).includes(target.toLowerCase());
}

export function resolveCaseInconsistencyContinuesTargetEntry(entries = [], qRaw = '') {
  const targets = extractCaseInconsistencyIdsFromCell(qRaw);
  if (targets.length !== 1) return null;
  const targetNorm = normalizeCaseInconsistencyId(targets[0]);
  if (!targetNorm) return null;
  let match = null;
  for (const entry of Array.isArray(entries) ? entries : []) {
    const entryNorm = normalizeCaseInconsistencyId(entry?.hash || entry?.entryHash);
    if (!entryNorm || entryNorm !== targetNorm) continue;
    if (match) return null;
    match = entry;
  }
  return match;
}

export function caseInconsistencyContinuesTargetMatchesNextEntry(qRaw = '', nextEntry = null) {
  const targets = extractCaseInconsistencyIdsFromCell(qRaw);
  if (targets.length !== 1 || !nextEntry) return false;
  return normalizeCaseInconsistencyId(targets[0]) === normalizeCaseInconsistencyId(nextEntry?.hash || nextEntry?.entryHash);
}

export function caseInconsistencyEntryPointsBackToCurrent(currentEntry = null, targetEntry = null) {
  const currentHash = normalizeCaseInconsistencyId(currentEntry?.hash || currentEntry?.entryHash || currentEntry?.rows?.[0]?.row?.[1]);
  if (!currentHash || !targetEntry?.rows?.length) return false;
  const fromIds = extractCaseInconsistencyIdsFromCell(targetEntry.rows[0]?.row?.[14]);
  return fromIds.some(id => normalizeCaseInconsistencyId(id) === currentHash);
}

export function caseInconsistencyEntryUsesAddressAsInput(entry = null, address = '') {
  const target = normalizeCell(address).toLowerCase();
  if (!entry || !target) return false;
  return (entry.rows || []).some(rowRef => normalizeCell(rowRef?.row?.[2]).toLowerCase() === target);
}

export function caseInconsistencyAnyOtherEntryUsesAddressAsInput(entries = [], currentIndex = -1, address = '') {
  const target = normalizeCell(address);
  if (!target) return false;
  return (Array.isArray(entries) ? entries : []).some((entry, index) => index !== currentIndex && caseInconsistencyEntryUsesAddressAsInput(entry, target));
}

export function caseInconsistencyPathLabelLooksLikeRootBase(value = '') {
  const label = normalizeCell(value);
  if (!label) return true;
  if (label.includes('->') || label.includes('→') || label.includes('.') || /\s/u.test(label)) return false;
  if (/^\d+$/.test(label)) return true;
  if (/^[A-Z]+$/.test(label)) return true;
  if (/^[A-Za-z0-9_:/.-]+-\d+$/.test(label)) return true;
  return false;
}

export function caseInconsistencyLineIsTransferNarrative(line = '') {
  return /^Funds\s+move\s+from\b.+?\s+into\b.+?\.?$/iu.test(normalizeCell(line).replace(/\s+/g, ' '));
}

export function caseInconsistencyEndpointAttributionEvidenceIsRowSpecific(entry = {}, rowRef = null) {
  const row = rowRef?.row || entry.rows?.[0]?.row || [];
  const toAddress = normalizeCell(row[3]);
  const attribution = normalizeCell(row[10]);
  if (!toAddress || !attribution) return true;
  const text = [row[9], row[11], ...(entry.rows || []).map(ref => ref?.row?.[22])]
    .map(value => normalizeCell(value))
    .filter(Boolean)
    .join(' ');
  const lower = text.toLowerCase();
  const addressMentioned = lower.includes(toAddress.toLowerCase());
  const explicitToolEvidence = /Tool\s*88\s+attribution|Tool\s*88:\s*Path\s+ends\s+at|action\s*=\s*manual-path-ending|Tool\s*88\s+automatic\s+decision|manual\s+path\s+ending/iu.test(text);
  const attributionMentioned = lower.includes(attribution.toLowerCase());
  return Boolean(explicitToolEvidence && (addressMentioned || attributionMentioned));
}

export function caseInconsistencyEntryIsDescendant(entry = {}, entries = []) {
  const firstRow = entry?.rows?.[0]?.row || [];
  const hash = normalizeCaseInconsistencyId(entry?.hash || firstRow[1]);
  if (normalizeCell(firstRow[14])) return true;
  const label = normalizeCell(firstRow[0]);
  if (label && !caseInconsistencyPathLabelLooksLikeRootBase(label)) return true;
  if (!hash) return false;
  return (Array.isArray(entries) ? entries : []).some(other => {
    if (other === entry) return false;
    return (other.rows || []).some(ref => extractCaseInconsistencyIdsFromCell(ref?.row?.[16]).some(id => normalizeCaseInconsistencyId(id) === hash));
  });
}

export function findCaseInconsistencyRowThatShouldEndInsteadOfContinue(entries = [], currentIndex = -1, currentEntry = null, continuedEntry = null) {
  if (!currentEntry?.rows || currentEntry.rows.length <= 1) return null;
  for (const rowRef of currentEntry.rows) {
    const row = Array.isArray(rowRef?.row) ? rowRef.row : [];
    const rowNotes = normalizeCell(row[9]).toLowerCase();
    if (!rowNotes.includes('path ends')) continue;
    const outputAddress = normalizeCell(row[3]);
    if (!outputAddress) continue;
    if (caseInconsistencyEntryUsesAddressAsInput(continuedEntry, outputAddress)) continue;
    if (caseInconsistencyAnyOtherEntryUsesAddressAsInput(entries, currentIndex, outputAddress)) continue;
    return rowRef;
  }
  return null;
}

export function firstCaseInconsistencyRowClearlyContinuesToResolvedEntry(currentEntry = null, continuedEntry = null) {
  const firstRow = currentEntry?.rows?.[0]?.row || [];
  const firstRowTo = normalizeCell(firstRow[3]);
  if (!firstRowTo) return false;
  return caseInconsistencyEntryUsesAddressAsInput(continuedEntry, firstRowTo) && caseInconsistencyEntryPointsBackToCurrent(currentEntry, continuedEntry);
}

export function anyOtherCaseInconsistencyEntryReferencesThisEntryAsFromHash(entries = [], currentIndex = -1, currentEntryHash = '') {
  const currentNorm = normalizeCaseInconsistencyId(currentEntryHash);
  if (!currentNorm) return false;
  return (Array.isArray(entries) ? entries : []).some((entry, index) => {
    if (index === currentIndex) return false;
    const fromIds = extractCaseInconsistencyIdsFromCell(entry?.rows?.[0]?.row?.[14]);
    return fromIds.some(id => normalizeCaseInconsistencyId(id) === currentNorm);
  });
}

export function buildCaseInconsistencySearchText(item = {}) {
  const parts = [
    item.label,
    item.entryId,
    item.rowNum,
    item.firstRowNotes,
    item.combinedNotes,
    item.attribution,
    item.fundsFollowed,
    item.fromRaw,
    item.continuesAsRaw,
    item.nextEntryHash,
    item.nextEntryLabel,
    ...(item.ruleTags || []),
    ...(item.issues || [])
  ];
  (item.fixes || []).forEach((fix) => parts.push(fix.title, fix.fieldLabel, fix.newValue, fix.reason));
  return parts.map(part => normalizeCell(part).toLowerCase()).filter(Boolean).join(' ');
}

export function buildCaseInconsistencyReviewBasisHash(items = []) {
  const payload = (Array.isArray(items) ? items : []).map(item => ({
    key: item.key,
    rowNum: item.rowNum,
    entryId: item.entryId,
    ruleTags: item.ruleTags,
    issues: item.issues,
    fixes: (item.fixes || []).map(fix => ({ code: fix.code, targetRowIndex: fix.targetRowIndex, colIndex: fix.colIndex, newValue: fix.newValue }))
  }));
  return `case-inconsistencies-${caseBuddyHashString(JSON.stringify(payload))}`;
}

export function makeCaseInconsistencyFix({ code, kind = 'judgement', title, targetRowIndex, colIndex, oldValue, newValue, reason }) {
  const columnLabel = Number.isInteger(colIndex) ? CASE_COLUMN_HEADERS[colIndex] || `Column ${colIndex + 1}` : '';
  return {
    id: `${code}-${targetRowIndex}-${colIndex}-${caseBuddyHashString(`${oldValue}=>${newValue}`)}`,
    code,
    kind,
    title,
    targetRowIndex,
    targetRowNum: Number.isInteger(targetRowIndex) ? targetRowIndex + 1 : '',
    colIndex,
    fieldLabel: columnLabel,
    oldValue: normalizeCell(oldValue),
    newValue: normalizeCell(newValue),
    reason: normalizeCell(reason),
    isSafe: kind === 'safe',
    isJudgement: kind !== 'safe'
  };
}

export function finalizeCaseInconsistencyItem(item = {}, reviewRecord = null) {
  item.issueCount = (item.ruleTags || []).length;
  item.safeFixCount = (item.fixes || []).filter(fix => fix.kind === 'safe').length;
  item.judgementFixCount = (item.fixes || []).filter(fix => fix.kind !== 'safe').length;
  item.hasAutoFixEligible = (item.fixes || []).length > 0;
  item.hasSafeAutoFixEligible = item.safeFixCount > 0;
  item.hasJudgementAutoFixEligible = item.judgementFixCount > 0;
  item.issueSignature = caseBuddyHashString(JSON.stringify({ issues: item.issues, fixes: item.fixes, q: item.continuesAsRaw, notes: item.combinedNotes }));
  item.key = `${item.baseKey}|${item.issueSignature}`;
  const resolvedMap = reviewRecord?.resolvedCardKeys && typeof reviewRecord.resolvedCardKeys === 'object' ? reviewRecord.resolvedCardKeys : {};
  item.isResolved = !!resolvedMap[item.key]?.resolvedAtUtc;
  item.searchText = buildCaseInconsistencySearchText(item);
  return item;
}

export function buildCaseInconsistencyReviewAnalysis(mainData = [], reviewRecord = null, options = {}) {
  const ruleOptions = normalizeCaseInconsistencyOptions(options);
  const data = normalizeCaseMatrix(mainData);
  const entries = parseCaseEntries(data);
  const knownIds = new Set();
  const idToLabel = new Map();

  entries.forEach((entry) => {
    const idNorm = normalizeCaseInconsistencyId(entry.hash);
    if (!idNorm) return;
    knownIds.add(idNorm);
    const label = normalizeCell(entry.rows?.[0]?.row?.[0]);
    if (label && !idToLabel.has(idNorm)) idToLabel.set(idNorm, label);
  });

  const items = [];

  entries.forEach((entry, entryIndex) => {
    const firstRowRef = entry.rows?.[0] || { rowIndex: entry.startRow, row: [] };
    const firstRow = firstRowRef.row || [];
    const nextEntry = entryIndex + 1 < entries.length ? entries[entryIndex + 1] : null;
    const label = normalizeCell(firstRow[0]);
    const entryId = normalizeCell(entry.hash || firstRow[1]);
    const entryRowNum = (Number.isInteger(firstRowRef.rowIndex) ? firstRowRef.rowIndex : entry.startRow) + 1;
    let qRaw = normalizeCell(firstRow[16]);
    let qLower = qRaw.toLowerCase();
    let firstRowNotes = normalizeCell(firstRow[9]);
    let combinedNotes = buildCaseInconsistencyCombinedNotes(entry.rows);
    let notesLower = combinedNotes.toLowerCase();
    const item = {
      baseKey: `${entryRowNum}|${entryId}`,
      key: `${entryRowNum}|${entryId}`,
      rowIndex: firstRowRef.rowIndex,
      rowNum: entryRowNum,
      label,
      entryId,
      entryIdShort: abbreviateCaseBuddyHash(entryId),
      firstRowNotes,
      combinedNotes,
      attribution: firstNonEmptyCaseInconsistencyEntryValue(entry.rows, 10),
      fundsFollowed: normalizeCell(firstRow[12]),
      fromRaw: normalizeCell(firstRow[14]),
      continuesAsRaw: qRaw,
      nextEntryHash: nextEntry ? normalizeCell(nextEntry.hash) : '',
      nextEntryLabel: nextEntry ? normalizeCell(nextEntry.rows?.[0]?.row?.[0]) : '',
      issues: [],
      ruleTags: [],
      fixes: [],
      hasAutoFixSkipped: false,
      skippedMessages: [],
      isResolved: false,
      searchText: ''
    };

    const addIssue = (ruleTag, message) => {
      item.ruleTags.push(ruleTag);
      item.issues.push(message);
    };
    const addDetailMessage = (message) => {
      item.skippedMessages.push(message);
      item.issues.push(message);
    };
    const registerFix = fix => item.fixes.push(fix);
    const refreshEntryState = () => {
      qRaw = normalizeCell(firstRow[16]);
      qLower = qRaw.toLowerCase();
      firstRowNotes = normalizeCell(firstRow[9]);
      combinedNotes = buildCaseInconsistencyCombinedNotes(entry.rows);
      notesLower = combinedNotes.toLowerCase();
    };

    let notesHasPathEnds = notesLower.includes('path ends');
    let qIsPathEnds = isCaseInconsistencyPathEndsText(qLower);
    let notesHasBranchesAs = notesLower.includes('branches as');
    const pathEndsNotesMismatch = entry.rows.length > 1
      ? caseInconsistencyEntryStillHasAnyPathEndsNotesToQMismatch(entry.rows)
      : (notesHasPathEnds && !qIsPathEnds);

    if (ruleOptions.rulePathEndsMismatch) {
      if (pathEndsNotesMismatch) {
        addIssue(CASE_INCONSISTENCY_RULES.PATH_ENDS_NOTES_TO_Q_MISMATCH, 'Notes contain "Path ends." but Column Q is not "Path ends." for at least one row in this entry.');
      }
      if (qIsPathEnds && !notesHasPathEnds) {
        addIssue(CASE_INCONSISTENCY_RULES.PATH_ENDS_Q_TO_NOTES_MISMATCH, 'Column Q is "Path ends." but Notes (Column J across entry) do not mention "Path ends."');
      }
    }

    if (ruleOptions.ruleBranchesAsVsPathEnds && notesHasBranchesAs && qIsPathEnds) {
      addIssue(CASE_INCONSISTENCY_RULES.BRANCHES_AS_VS_PATH_ENDS, 'Notes contain "Branches as" but Column Q says "Path ends." These usually should not coexist.');
    }

    if (ruleOptions.ruleUnknownHashes) {
      const fromIds = extractCaseInconsistencyIdsFromCell(item.fromRaw).map(normalizeCaseInconsistencyId).filter(Boolean);
      const continuesIds = isCaseInconsistencyContinuesAsText(qLower) ? extractCaseInconsistencyIdsFromCell(qRaw).map(normalizeCaseInconsistencyId).filter(Boolean) : [];
      const missing = [...new Set([...fromIds, ...continuesIds])].filter(id => !knownIds.has(id));
      if (missing.length > 0) {
        addIssue(CASE_INCONSISTENCY_RULES.UNKNOWN_REFERENCED_IDS, `From/Continues reference ID(s) not found in any entry ID (Column B): ${missing.join(', ')}.`);
      }
    }

    if (ruleOptions.rulePathEndsButNoFundsFollowed && qIsPathEnds && !item.fundsFollowed) {
      addIssue(CASE_INCONSISTENCY_RULES.PATH_ENDS_BUT_NO_FUNDS_FOLLOWED, 'Column Q is "Path ends." but Column M (Funds Followed) is empty on this entry\'s first row.');
    }

    if (ruleOptions.rulePossibleCopiedEndpointAttribution) {
      const descendantEntry = caseInconsistencyEntryIsDescendant(entry, entries);
      (entry.rows || []).forEach((rowRef) => {
        const row = rowRef?.row || [];
        const rowQIsPathEnds = isCaseInconsistencyPathEndsText(row[16]);
        const attribution = normalizeCell(row[10]);
        if (!rowQIsPathEnds || !attribution) return;
        if (!descendantEntry && !normalizeCell(row[14])) return;
        if (caseInconsistencyEndpointAttributionEvidenceIsRowSpecific(entry, rowRef)) return;
        const rowNum = Number.isInteger(rowRef?.rowIndex) ? rowRef.rowIndex + 1 : entryRowNum;
        const toAddress = normalizeCell(row[3]);
        addIssue(
          CASE_INCONSISTENCY_RULES.POSSIBLE_COPIED_ENDPOINT_ATTRIBUTION,
          `Possible copied attribution: Column K says ${attribution}, but row-specific attribution evidence was not found for this To Address${toAddress ? ` (${toAddress})` : ''} on row ${rowNum}.`
        );
      });
    }

    let continuesMismatchTriggered = false;
    let continuesMismatchTargetRaw = '';
    if (ruleOptions.ruleContinuesAsSingleButNextEntryMismatch && isCaseInconsistencyContinuesAsText(qLower)) {
      const rawTargets = extractCaseInconsistencyIdsFromCell(qRaw);
      if (rawTargets.length === 1 && nextEntry) {
        const targetRaw = rawTargets[0];
        const targetNorm = normalizeCaseInconsistencyId(targetRaw);
        const nextIdRaw = normalizeCell(nextEntry.hash);
        const nextIdNorm = normalizeCaseInconsistencyId(nextIdRaw);
        if (targetNorm && nextIdNorm && targetNorm !== nextIdNorm) {
          const targetLabel = idToLabel.get(targetNorm) || '';
          const clarified = notesLower.includes('continues as') && (
            caseInconsistencyNotesMentionsId(combinedNotes, notesLower, targetRaw) ||
            (targetLabel && targetLabel.length > 1 && caseInconsistencyNotesMentionsLabel(combinedNotes, notesLower, targetLabel))
          );
          if (!clarified) {
            continuesMismatchTriggered = true;
            continuesMismatchTargetRaw = targetRaw;
            addIssue(CASE_INCONSISTENCY_RULES.CONTINUES_AS_SINGLE_NEXT_ENTRY_MISMATCH, `Column Q says "Continues as ${targetRaw}" (single target), but the next entry below is "${nextIdRaw}". Notes do not clearly state this continuation.`);
          }
        }
      }
    }

    if (continuesMismatchTriggered) {
      if (notesLower.includes('path ends')) {
        item.hasAutoFixSkipped = true;
        addDetailMessage('Safe auto-fix skipped: entry notes already contain "Path ends." Manual review is recommended.');
      } else if (firstRowNotes.toLowerCase().includes('continues as')) {
        item.hasAutoFixSkipped = true;
        addDetailMessage('Safe auto-fix skipped: first-row Notes already contain "Continues as".');
      } else {
        const nextNotes = buildCaseInconsistencyNotesWithContinues(firstRowNotes, `Continues as ${continuesMismatchTargetRaw}`);
        if (normalizeCell(nextNotes) !== normalizeCell(firstRowNotes)) {
          registerFix(makeCaseInconsistencyFix({
            code: CASE_INCONSISTENCY_FIX_CODES.SAFE_ADD_CONTINUES_NOTE,
            kind: 'safe',
            title: 'Add explicit continuation to first-row Notes',
            targetRowIndex: firstRowRef.rowIndex,
            colIndex: 9,
            oldValue: firstRowNotes,
            newValue: nextNotes,
            reason: 'Single-target Continues As points somewhere other than the next visible entry, and the notes do not already clarify that continuation.'
          }));
        }
      }
    }

    refreshEntryState();
    notesHasPathEnds = notesLower.includes('path ends');
    qIsPathEnds = isCaseInconsistencyPathEndsText(qLower);
    const resolvedContinuedEntry = resolveCaseInconsistencyContinuesTargetEntry(entries, qRaw);
    const targetPointsBack = resolvedContinuedEntry ? caseInconsistencyEntryPointsBackToCurrent(entry, resolvedContinuedEntry) : false;
    const rowToEndCandidate = ruleOptions.rulePathEndsMismatch && notesHasPathEnds && !qIsPathEnds && isCaseInconsistencyContinuesAsText(qLower) && entry.rows.length > 1 && resolvedContinuedEntry && targetPointsBack
      ? findCaseInconsistencyRowThatShouldEndInsteadOfContinue(entries, entryIndex, entry, resolvedContinuedEntry)
      : null;

    if (rowToEndCandidate) {
      const currentQ = normalizeCell(rowToEndCandidate.row?.[16]);
      if (!isCaseInconsistencyPathEndsText(currentQ)) {
        registerFix(makeCaseInconsistencyFix({
          code: CASE_INCONSISTENCY_FIX_CODES.JUDGEMENT_SET_SUBROW_Q_PATH_ENDS,
          kind: 'judgement',
          title: 'Set ending subrow Column Q to "Path ends."',
          targetRowIndex: rowToEndCandidate.rowIndex,
          colIndex: 16,
          oldValue: currentQ,
          newValue: 'Path ends.',
          reason: 'This multi-row entry appears to carry the same continuation across multiple outputs, but this row\'s notes already indicate a path ending and its output address is not used later.'
        }));
      }
    }

    if (ruleOptions.rulePathEndsMismatch && continuesMismatchTriggered && notesHasPathEnds && !qIsPathEnds && isCaseInconsistencyContinuesAsText(qLower) && entry.rows.length > 1 && resolvedContinuedEntry && targetPointsBack && rowToEndCandidate && firstCaseInconsistencyRowClearlyContinuesToResolvedEntry(entry, resolvedContinuedEntry)) {
      const targets = extractCaseInconsistencyIdsFromCell(qRaw);
      if (!firstRowNotes.toLowerCase().includes('continues as') && !firstRowNotes.toLowerCase().includes('path ends') && targets.length === 1) {
        const nextNotes = buildCaseInconsistencyNotesWithExplicitContinuation(firstRowNotes, targets[0]);
        if (normalizeCell(nextNotes) !== normalizeCell(firstRowNotes)) {
          registerFix(makeCaseInconsistencyFix({
            code: CASE_INCONSISTENCY_FIX_CODES.JUDGEMENT_ADD_EXPLICIT_CONTINUATION,
            kind: 'judgement',
            title: 'Add explicit continuation to first-row Notes',
            targetRowIndex: firstRowRef.rowIndex,
            colIndex: 9,
            oldValue: firstRowNotes,
            newValue: nextNotes,
            reason: 'The first-row output clearly continues into the resolved target entry, while a different subrow appears to account for the path-ending narrative.'
          }));
        }
      }
    }

    if (ruleOptions.rulePathEndsMismatch && notesHasPathEnds && !qIsPathEnds && isCaseInconsistencyContinuesAsText(qLower)) {
      const continuesTargetMatchesNext = caseInconsistencyContinuesTargetMatchesNextEntry(qRaw, nextEntry);
      const nextPointsBack = nextEntry ? caseInconsistencyEntryPointsBackToCurrent(entry, nextEntry) : false;
      const notesExplicitlyBranch = notesLower.includes('branches as');
      if (continuesTargetMatchesNext && nextPointsBack && !notesExplicitlyBranch) {
        const nextNotes = buildCaseInconsistencyNotesWithoutPathEnds(firstRowNotes);
        if (normalizeCell(nextNotes) !== normalizeCell(firstRowNotes)) {
          registerFix(makeCaseInconsistencyFix({
            code: CASE_INCONSISTENCY_FIX_CODES.JUDGEMENT_REMOVE_STRAY_PATH_ENDS,
            kind: 'judgement',
            title: 'Remove stray "Path ends." from first-row Notes',
            targetRowIndex: firstRowRef.rowIndex,
            colIndex: 9,
            oldValue: firstRowNotes,
            newValue: nextNotes,
            reason: 'Notes say "Path ends." but Column Q continues into the linked next entry, and that next entry points back via From Hash.'
          }));
        }
      }
    }

    if (ruleOptions.rulePathEndsMismatch && qIsPathEnds && !notesHasPathEnds) {
      const notesContradictEnding = notesLower.includes('continues as') || notesLower.includes('branches as');
      const referencedElsewhere = anyOtherCaseInconsistencyEntryReferencesThisEntryAsFromHash(entries, entryIndex, entryId);
      if (!notesContradictEnding && !referencedElsewhere) {
        const nextNotes = buildCaseInconsistencyNotesWithPathEnds(firstRowNotes);
        if (normalizeCell(nextNotes) !== normalizeCell(firstRowNotes)) {
          registerFix(makeCaseInconsistencyFix({
            code: CASE_INCONSISTENCY_FIX_CODES.JUDGEMENT_ADD_PATH_ENDS_NOTE,
            kind: 'judgement',
            title: 'Add "Path ends." to first-row Notes',
            targetRowIndex: firstRowRef.rowIndex,
            colIndex: 9,
            oldValue: firstRowNotes,
            newValue: nextNotes,
            reason: 'Column Q already says "Path ends." and the notes do not explicitly describe a continuation or branch.'
          }));
        }
      }
    }

    item.firstRowNotes = firstRowNotes;
    item.combinedNotes = combinedNotes;
    item.continuesAsRaw = qRaw;
    if (item.ruleTags.length > 0) {
      items.push(finalizeCaseInconsistencyItem(item, reviewRecord));
    }
  });

  items.sort((left, right) => {
    if (left.isResolved !== right.isResolved) return left.isResolved ? 1 : -1;
    if (left.hasAutoFixEligible !== right.hasAutoFixEligible) return left.hasAutoFixEligible ? -1 : 1;
    return (left.rowNum || 0) - (right.rowNum || 0);
  });

  const totalIssueCount = items.reduce((sum, item) => sum + (item.issueCount || 0), 0);
  const safeFixCount = items.reduce((sum, item) => sum + (item.safeFixCount || 0), 0);
  const judgementFixCount = items.reduce((sum, item) => sum + (item.judgementFixCount || 0), 0);
  const pendingItems = items.filter(item => !item.isResolved);
  const currentBasisHash = buildCaseInconsistencyReviewBasisHash(items);
  const confirmedBasisHash = normalizeCell(reviewRecord?.basisHash);
  const isCurrent = !!currentBasisHash && !!confirmedBasisHash && currentBasisHash === confirmedBasisHash;
  let summary = '';
  if (items.length === 0) {
    summary = 'No Tool 71-style inconsistencies were found with the enabled checks.';
  } else {
    summary = `Found ${totalIssueCount} issue${totalIssueCount === 1 ? '' : 's'} across ${items.length} card${items.length === 1 ? '' : 's'}; ${pendingItems.length} card${pendingItems.length === 1 ? '' : 's'} still need resolution.`;
  }
  if (safeFixCount) summary += ` ${safeFixCount} safe fix${safeFixCount === 1 ? '' : 'es'} available.`;
  if (judgementFixCount) summary += ` ${judgementFixCount} judgement fix${judgementFixCount === 1 ? '' : 'es'} available.`;

  return {
    entriesAnalyzed: entries.length,
    items,
    pendingItems,
    resolvedItems: items.filter(item => item.isResolved),
    itemCount: items.length,
    pendingCount: pendingItems.length,
    resolvedCount: items.length - pendingItems.length,
    totalIssueCount,
    safeFixCount,
    judgementFixCount,
    currentBasisHash,
    confirmedBasisHash,
    isCurrent,
    pass: items.length === 0 || pendingItems.length === 0,
    summary,
    ruleOptions
  };
}

export function buildCaseInconsistencyAuditRows(analysis = {}, actionLabel = '') {
  const rows = [
    ['Case Buddy Inconsistency Review Audit', '', '', '', '', '', '', '', '', 'Generated by Review and resolve case inconsistencies. Audit only; Main Data is the source of truth.'],
    ['Action', actionLabel || 'Review refreshed', 'Entries Analyzed', analysis.entriesAnalyzed || 0, 'Cards', analysis.itemCount || 0, 'Pending', analysis.pendingCount || 0, 'Safe Fixes', analysis.safeFixCount || 0],
    ['Row', 'Path Label', 'Transaction Hash', 'Issues', 'Safe Fixes', 'Judgement Fixes', 'Resolved', 'Column Q', 'Attribution', 'Notes']
  ];
  (analysis.items || []).forEach((item) => {
    rows.push([
      item.rowNum || '',
      item.label || '',
      item.entryId || '',
      (item.issues || []).join(' | '),
      item.safeFixCount || 0,
      item.judgementFixCount || 0,
      item.isResolved ? 'Resolved' : 'Pending',
      item.continuesAsRaw || '',
      item.attribution || '',
      item.combinedNotes || ''
    ]);
  });
  return rows;
}
